const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Trades the chart can render as diamond markers; needs date + notional for density logic.
 */
export type BlockTradeNotional = {
  date: string;
  time?: string;
  dollar_value: number;
};

function tradeTimestampMs(t: BlockTradeNotional): number {
  if (t.time && /^\d/.test(t.time)) {
    const d = new Date(`${t.date}T${t.time}`);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return new Date(`${t.date}T12:00:00`).getTime();
}

/**
 * If any rolling time window of `windowDays` contains more than `maxInWindow` trades, repeatedly
 * remove the single lowest-`dollar_value` trade within such a window until every window is at
 * or under the cap. This prevents marker crowding (e.g. many block trades in a few weeks) while
 * keeping the top notionals in each busy segment.
 */
export function capDenseBlockTradesByRollingWindow<T extends BlockTradeNotional>(
  trades: T[],
  options?: { windowDays?: number; maxInWindow?: number }
): T[] {
  const windowDays = options?.windowDays ?? 30;
  const maxInWindow = options?.maxInWindow ?? 10;
  const windowMs = windowDays * MS_PER_DAY;

  if (trades.length === 0) return [];

  let remaining: T[] = trades.slice();

  for (;;) {
    const indexed = remaining
      .map((tr) => ({ tr, t: tradeTimestampMs(tr) }))
      .sort((a, b) => a.t - b.t);

    let toRemove: T | null = null;
    for (let s = 0; s < indexed.length; s++) {
      const startT = indexed[s].t;
      let e = s;
      while (e + 1 < indexed.length && indexed[e + 1].t - startT <= windowMs) {
        e += 1;
      }
      if (e - s + 1 <= maxInWindow) continue;

      let minAt = s;
      for (let k = s + 1; k <= e; k++) {
        const a = indexed[minAt].tr.dollar_value;
        const b = indexed[k].tr.dollar_value;
        if (b < a || (b === a && indexed[k].t < indexed[minAt].t)) {
          minAt = k;
        }
      }
      toRemove = indexed[minAt].tr;
      break;
    }

    if (toRemove === null) break;
    remaining = remaining.filter((x) => x !== toRemove);
  }

  return remaining;
}
